import classNames from "classnames";
import { FC, ReactElement, useCallback, useEffect, useMemo, useState } from "react";

import { ReactComponent as InfoIcon } from "assets/images/common/info.svg";
import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as ReloadIcon } from "assets/images/common/reload.svg";

import { BaseOption, UserRole } from "entities/common";
import { IncidentRow } from "entities/incidents";

import PageTemplate from "modules/common/PageTemplate";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import Card from "components/Card";
import DateRangePicker, { DateRangeValue } from "components/DateRangePicker";
import SearchInput from "components/SearchInput";
import Select from "components/Select";
import Table from "components/Table";

import { AssetsApi, CompaniesApi, PortfoliosApi } from "services/http";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";
import { RootStore } from "store";
import { useAppDispatch, useAppSelector } from "store/hooks";
import {
  getIncidentListRequest,
  getIncidentStatisticsRequest,
  resetIncidentList,
  resetIncidentStatistics,
} from "store/slices/incidentsSlice";
import { QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";
import { formatIncidentTime } from "utils/incidentTime";

import styles from "./style.module.scss";

const PAGE_SIZE = 20;

const STAT_CARDS: { title: string; subtitle: string; key: "total_active" | "active_high" | "resolved_last_30d" }[] = [
  { title: "Total active incidents", subtitle: "Open, all severities", key: "total_active" },
  { title: "Active & high priority", subtitle: "Open, high", key: "active_high" },
  { title: "Resolved", subtitle: "Successfully completed in the last month", key: "resolved_last_30d" },
];

const INCIDENT_TYPE_OPTIONS: BaseOption[] = [
  { value: "", label: "All types" },
  { value: "Air Quality - CO₂", label: "Air Quality - CO₂" },
  { value: "Air Quality - HCHO", label: "Air Quality - HCHO" },
  { value: "Air Quality - TVOC", label: "Air Quality - TVOC" },
  { value: "Air Quality - PM2.5", label: "Air Quality - PM2.5" },
  { value: "Air Quality - PM10", label: "Air Quality - PM10" },
  { value: "Thermal Comfort - Temperature", label: "Thermal Comfort - Temperature" },
  { value: "Thermal Comfort - Humidity", label: "Thermal Comfort - Humidity" },
];

const SEVERITY_OPTIONS: BaseOption[] = [
  { value: "", label: "All severities" },
  { value: "Medium", label: "Medium" },
  { value: "High", label: "High" },
];

const STATUS_OPTIONS: BaseOption[] = [
  { value: "", label: "All statuses" },
  { value: "active", label: "Active" },
  { value: "resolved", label: "Resolved" },
  { value: "device_removed", label: "Device removed" },
];

const SELECT_PAGE_SIZE = 50;

const STATUS_LABELS: Record<string, string> = {
  active: "Active",
  resolved: "Resolved",
  device_removed: "Device removed",
};

const IncidentTimeCell: FC<{ iso: string | null; now: number; dash: boolean }> = ({ iso, now, dash }) => {
  if (dash || !iso) {
    return <>-</>;
  }
  const { text, tooltip } = formatIncidentTime(iso, now);
  if (!tooltip) {
    return <span>{text}</span>;
  }
  return (
    <span className={styles.timeCell}>
      {text}
      <span className={styles.timeInfo}>
        <InfoIcon />
        <span className={styles.timeTooltip}>{tooltip}</span>
      </span>
    </span>
  );
};

const IncidentsListPage: FC = () => {
  const dispatch = useAppDispatch();
  const { list, meta, statistics, totalIncidents, hasIncidents, listLoaded, isListLoading } = useAppSelector(
    (s: RootStore) => s.incidents,
  );
  const isAdmin = useAppSelector((s: RootStore) => s.auth.user?.role) === UserRole.ADMINISTRATOR;

  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [dateRange, setDateRange] = useState<DateRangeValue>({ from: null, to: null });
  const [page, setPage] = useState(1);
  const [now, setNow] = useState(() => Date.now());

  const [companyId, setCompanyId] = useState("");
  const [companyLabel, setCompanyLabel] = useState("All companies");
  const [portfolioId, setPortfolioId] = useState("");
  const [portfolioLabel, setPortfolioLabel] = useState("All portfolios");
  const [assetId, setAssetId] = useState("");
  const [assetLabel, setAssetLabel] = useState("All assets");
  const [typeValue, setTypeValue] = useState("");
  const [severityValue, setSeverityValue] = useState("");
  const [statusValue, setStatusValue] = useState("");

  const debouncedSearch = useDebounce(search, 500);

  const requestList = useCallback(
    (silent = false) => {
      dispatch(
        getIncidentListRequest(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: debouncedSearch.trim() || undefined,
            [QUERY_PARAMS.COMPANY_ID]: companyId ? Number(companyId) : undefined,
            [QUERY_PARAMS.PORTFOLIO_ID]: portfolioId ? Number(portfolioId) : undefined,
            [QUERY_PARAMS.ASSET_ID]: assetId ? Number(assetId) : undefined,
            [QUERY_PARAMS.INCIDENT_TYPE]: typeValue || undefined,
            [QUERY_PARAMS.SEVERITY]: severityValue || undefined,
            [QUERY_PARAMS.STATUS]: statusValue || undefined,
            [QUERY_PARAMS.TIME_DETECTED_FROM]: dateRange.from || undefined,
            [QUERY_PARAMS.TIME_DETECTED_TO]: dateRange.to || undefined,
            [QUERY_PARAMS.PAGE]: page,
            [QUERY_PARAMS.PAGE_SIZE]: PAGE_SIZE,
          }),
          silent ? { silent: true } : undefined,
        ),
      );
    },
    [
      dispatch,
      debouncedSearch,
      companyId,
      portfolioId,
      assetId,
      typeValue,
      severityValue,
      statusValue,
      dateRange.from,
      dateRange.to,
      page,
    ],
  );

  useEffect(() => {
    setPage(1);
  }, [
    debouncedSearch,
    companyId,
    portfolioId,
    assetId,
    typeValue,
    severityValue,
    statusValue,
    dateRange.from,
    dateRange.to,
  ]);

  useEffect(() => {
    requestList();
    dispatch(getIncidentStatisticsRequest(undefined));
  }, [requestList, dispatch]);

  const reload = useCallback(() => {
    requestList(true);
    dispatch(getIncidentStatisticsRequest(undefined, { silent: true }));
    setNow(Date.now());
  }, [requestList, dispatch]);

  useRealtime(["incidents"], reload);

  useEffect(() => {
    return () => {
      dispatch(resetIncidentList());
      dispatch(resetIncidentStatistics());
    };
  }, [dispatch]);

  const searchCompanies = useCallback(async (searchTerm: string, pageNum: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: searchTerm || undefined,
          [QUERY_PARAMS.PAGE]: pageNum,
          [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
        }),
      );
      const fetched: BaseOption[] = (data?.items ?? []).map((c) => ({
        value: String(c.id),
        label: c.name,
      }));
      const showAllSentinel = pageNum === 1 && !searchTerm.trim();
      const options: BaseOption[] = showAllSentinel
        ? [{ value: "", label: "All companies" }, ...fetched]
        : fetched;
      return { options, hasMore: (data?.total ?? 0) > pageNum * SELECT_PAGE_SIZE };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const searchPortfolios = useCallback(
    async (searchTerm: string, pageNum: number) => {
      try {
        const { data } = await PortfoliosApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: searchTerm || undefined,
            [QUERY_PARAMS.PAGE]: pageNum,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            ...(companyId ? { [QUERY_PARAMS.COMPANY_ID]: Number(companyId) } : {}),
          }),
        );
        const fetched: BaseOption[] = (data?.items ?? []).map((p) => ({
          value: String(p.id),
          label: p.name,
          companyId: p.company_id,
          companyName: p.company_name,
        }));
        const showAllSentinel = pageNum === 1 && !searchTerm.trim();
        const options: BaseOption[] = showAllSentinel
          ? [{ value: "", label: "All portfolios" }, ...fetched]
          : fetched;
        return { options, hasMore: (data?.total ?? 0) > pageNum * SELECT_PAGE_SIZE };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [companyId],
  );

  const searchAssets = useCallback(
    async (searchTerm: string, pageNum: number) => {
      try {
        const { data } = await AssetsApi.getList(
          QueryHelper.getQueryString({
            [QUERY_PARAMS.SEARCH]: searchTerm || undefined,
            [QUERY_PARAMS.PAGE]: pageNum,
            [QUERY_PARAMS.PAGE_SIZE]: SELECT_PAGE_SIZE,
            ...(portfolioId ? { [QUERY_PARAMS.PORTFOLIO_ID]: Number(portfolioId) } : {}),
            ...(companyId ? { [QUERY_PARAMS.COMPANY_ID]: Number(companyId) } : {}),
          }),
        );
        const fetched: BaseOption[] = (data?.items ?? []).map((a) => ({
          value: String(a.id),
          label: a.name,
          portfolioId: a.portfolio_id,
          portfolioName: a.portfolio_name,
          companyId: a.company_id,
          companyName: a.company_name,
        }));
        const showAllSentinel = pageNum === 1 && !searchTerm.trim();
        const options: BaseOption[] = showAllSentinel
          ? [{ value: "", label: "All assets" }, ...fetched]
          : fetched;
        return { options, hasMore: (data?.total ?? 0) > pageNum * SELECT_PAGE_SIZE };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [companyId, portfolioId],
  );

  const handleCompanyFilterChange = useCallback(
    (value: string, label: string) => {
      setCompanyId(value);
      setCompanyLabel(value ? label : "All companies");
      if (portfolioId) {
        setPortfolioId("");
        setPortfolioLabel("All portfolios");
      }
      if (assetId) {
        setAssetId("");
        setAssetLabel("All assets");
      }
    },
    [assetId, portfolioId],
  );

  const handlePortfolioFilterChange = useCallback(
    (value: string, label: string, option?: BaseOption) => {
      setPortfolioId(value);
      setPortfolioLabel(value ? label : "All portfolios");
      if (value && !companyId && option?.companyId) {
        setCompanyId(String(option.companyId));
        setCompanyLabel((option.companyName as string) ?? "");
      }
      if (assetId) {
        setAssetId("");
        setAssetLabel("All assets");
      }
    },
    [assetId, companyId],
  );

  const handleAssetFilterChange = useCallback((value: string, label: string, option?: BaseOption) => {
    setAssetId(value);
    setAssetLabel(value ? label : "All assets");
    if (value && option?.portfolioId) {
      setPortfolioId(String(option.portfolioId));
      setPortfolioLabel((option.portfolioName as string) ?? "");
    }
    if (value && option?.companyId) {
      setCompanyId(String(option.companyId));
      setCompanyLabel((option.companyName as string) ?? "");
    }
  }, []);

  const handleTypeFilterChange = useCallback((value: string) => {
    setTypeValue(value);
  }, []);

  const handleSeverityFilterChange = useCallback((value: string) => {
    setSeverityValue(value);
  }, []);

  const handleStatusFilterChange = useCallback((value: string) => {
    setStatusValue(value);
  }, []);

  const handleClearFilters = () => {
    setCompanyId("");
    setCompanyLabel("All companies");
    setPortfolioId("");
    setPortfolioLabel("All portfolios");
    setAssetId("");
    setAssetLabel("All assets");
    setTypeValue("");
    setSeverityValue("");
    setStatusValue("");
    setDateRange({ from: null, to: null });
    setPage(1);
  };

  const tableHeaders = useMemo(() => {
    const headers = [
      { label: "Company", key: "company" },
      { label: "Portfolio & Asset", key: "portfolio" },
      { label: "Address", key: "address" },
      { label: "Location", key: "location" },
      { label: "Type", key: "type" },
      { label: "Incident", key: "incident" },
      { label: "Severity", key: "severity" },
      { label: "Time detected", key: "timeDetected" },
      { label: "Duration", key: "duration" },
      { label: "Time resolved", key: "timeResolved" },
      { label: "Status", key: "status" },
    ];
    return isAdmin ? headers : headers.filter((h) => h.key !== "company");
  }, [isAdmin]);

  const tableData = useMemo(
    () =>
      list.map((incident: IncidentRow) => {
        const row: Record<string, string | number | ReactElement> = {
          portfolio: (
            <div className={styles.portfolio}>
              <span className={styles.portfolioName}>{incident.portfolio}</span>
              <span className={styles.portfolioAsset}>{incident.asset}</span>
            </div>
          ),
          address: <span className={styles.address}>{incident.address}</span>,
          location: <span className={styles.location}>{incident.location}</span>,
          type: <span className={styles.type}>{incident.type}</span>,
          incident: <span className={styles.incident}>{incident.incident}</span>,
          severity: (
            <span className={classNames(styles.severity, styles[`severity-${incident.severity.toLowerCase()}`])}>
              {incident.severity}
            </span>
          ),
          timeDetected: <IncidentTimeCell iso={incident.time_detected} now={now} dash={false} />,
          duration: incident.duration,
          timeResolved: (
            <IncidentTimeCell
              iso={incident.time_resolved}
              now={now}
              dash={incident.status === "active" || incident.status === "device_removed"}
            />
          ),
          status: (
            <span className={classNames(styles.status, styles[`status-${incident.status}`])}>
              {STATUS_LABELS[incident.status]}
            </span>
          ),
        };
        if (isAdmin) {
          row.company = <span className={styles.company}>{incident.company}</span>;
        }
        return row;
      }),
    [list, now, isAdmin],
  );

  const totalPages = Math.ceil(meta.total / meta.page_size) || 1;

  return (
    <PageTemplate title="Incidents" description="Monitor and manage all incidents" className={styles.page}>
      <Card className={styles.mainCard}>
        {hasIncidents && (
          <div className={styles.stats}>
            {STAT_CARDS.map((card) => (
              <div key={card.title} className={styles.statCard}>
                <span className={styles.statTitle}>{card.title}</span>
                <span className={styles.statValue}>{statistics[card.key]}</span>
                <span className={styles.statSubtitle}>{card.subtitle}</span>
              </div>
            ))}
          </div>
        )}

        <div className={styles.search}>
          <SearchInput
            name="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search incidents..."
            className={styles.searchInput}
          />

          <Button variant="secondary" className={styles.filtersToggle} onClick={() => setShowFilters((prev) => !prev)}>
            {showFilters ? "Close filters" : "Open filters"}
          </Button>
        </div>

        {showFilters && (
          <div className={styles.filters}>
            <div className={styles.filtersWrap}>
              {isAdmin && (
                <AsyncSearchableSelect
                  variant="secondary"
                  value={companyId}
                  selectedLabel={companyLabel}
                  onSearch={searchCompanies}
                  onChange={handleCompanyFilterChange}
                  placeholder="All companies"
                  searchPlaceholder="Search"
                  className={styles.filter}
                />
              )}

              <AsyncSearchableSelect
                variant="secondary"
                value={portfolioId}
                selectedLabel={portfolioLabel}
                onSearch={searchPortfolios}
                onChange={handlePortfolioFilterChange}
                placeholder="All portfolios"
                searchPlaceholder="Search"
                className={styles.filter}
              />

              <AsyncSearchableSelect
                variant="secondary"
                value={assetId}
                selectedLabel={assetLabel}
                onSearch={searchAssets}
                onChange={handleAssetFilterChange}
                placeholder="All assets"
                searchPlaceholder="Search"
                className={styles.filter}
              />

              <Select
                variant="secondary"
                options={INCIDENT_TYPE_OPTIONS}
                value={typeValue}
                onChange={handleTypeFilterChange}
                placeholder="All types"
                className={styles.filter}
              />

              <Select
                variant="secondary"
                options={SEVERITY_OPTIONS}
                value={severityValue}
                onChange={handleSeverityFilterChange}
                placeholder="All severities"
                className={styles.filter}
              />

              <Select
                variant="secondary"
                options={STATUS_OPTIONS}
                value={statusValue}
                onChange={handleStatusFilterChange}
                placeholder="All statuses"
                className={styles.filter}
              />

              <DateRangePicker value={dateRange} onChange={setDateRange} className={styles.dateRange} />
            </div>

            <Button form="circle" variant="white" className={styles.reload} onClick={handleClearFilters}>
              <ReloadIcon />
            </Button>

            <Button variant="white" className={styles.clearFilters} onClick={handleClearFilters}>
              Clear filters
            </Button>
          </div>
        )}

        {hasIncidents && (
          <p className={styles.tableTitle}>
            All Incidents <span className={styles.tableTitleCount}>{totalIncidents}</span>
          </p>
        )}

        {listLoaded && !hasIncidents ? (
          <div className={styles.notFound}>
            <NotFoundIcon />
            <p>No incidents yet</p>
          </div>
        ) : listLoaded && list.length === 0 ? (
          <div className={styles.notFound}>
            <NotFoundIcon />
            <p>No incidents found</p>
          </div>
        ) : (
          <div className={styles.tableScroll}>
            <Table
              headers={tableHeaders}
              data={tableData}
              pagination={{ total: totalPages, page, onPageChange: setPage }}
              isLoading={isListLoading}
              className={styles.table}
            />
          </div>
        )}
      </Card>
    </PageTemplate>
  );
};

export default IncidentsListPage;
